import type {
  FormSchemaVersion,
  BespokeTemplateResponse,
  BespokeTemplateRevision,
} from '../../services/api/types.gen';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  collectSectionLayoutCoverageIssues as collectSectionLayoutCoverageIssuesShared,
  validateLayoutJsonForSection as validateLayoutJsonForSectionShared,
} from '../../components/oasis/bespoke/layout/validation/validateSectionLayout';
import type { Question } from '../../components/oasis/types';
import { bespokeTemplateLeafKey } from './bespokeTemplatesImport';
import type {
  FormTreeQuestion,
  LayoutValidationResult,
  SectionLayoutCoverageIssues,
  SectionLayoutState,
} from './formSchemaTypes';

/**
 * User-facing message for a bespoke form-schema failure.
 *
 * Prefers API `detail` / network hints via {@link getUserFacingApiMessage}.
 * That helper intentionally never surfaces `Error.message`, so local throws
 * (e.g. layout validation before PATCH) fall through to `err.message` here.
 *
 * The `err.message` fallback is gated on the error having no HTTP status, so
 * only genuinely local throws surface their message. HTTP errors (e.g. a 5xx
 * whose `getUserFacingApiMessage` intentionally returns nothing, or an Axios
 * error whose `.message` is "Request failed with status code 500") keep the
 * generic copy instead of leaking a raw transport string.
 */
export function bespokeApiErrorMessage(err: unknown): string {
  const fromApi = getUserFacingApiMessage(err, '');
  if (fromApi) return fromApi;
  if (
    getHttpStatus(err) === undefined &&
    err instanceof Error &&
    err.message.trim()
  ) {
    return err.message.trim();
  }
  return 'Something went wrong. Please try again.';
}

export function isEffective(v: FormSchemaVersion): boolean {
  const now = new Date();
  const from = v.effective_from ? new Date(v.effective_from) : null;
  const until = v.effective_until ? new Date(v.effective_until) : null;
  if (from && now < from) return false;
  if (until && now > until) return false;
  return true;
}

export function schemaVersionKey(v: FormSchemaVersion): string {
  return `${v.name}::${v.version ?? ''}`;
}

export function schemaVersionLabel(v: FormSchemaVersion): string {
  return v.version ? `${v.name} (${v.version})` : v.name;
}

/** Stable key for a schema version name + section. Uses `::` like {@link schemaVersionKey}. */
export function layoutGenerationKey(
  schemaVersionName: string,
  sectionName: string
): string {
  return `${schemaVersionName}::${sectionName}`;
}

/**
 * Order-independent fingerprint of a `/bespoke-templates/revisions` poll.
 * Changes when a row appears or its `updated_at` moves, so the live-refresh
 * loop refetches the pin's layouts only after an MCP push.
 */
export function bespokeTemplateRevisionsFingerprint(
  revisions: readonly BespokeTemplateRevision[]
): string {
  return revisions
    .map(r => `${r.id}:${r.updated_at}`)
    .sort()
    .join('|');
}

/**
 * Section-layout validation moved to
 * `components/oasis/bespoke/layout/validation/validateSectionLayout` so the
 * generation loop and the MCP CLI can run the same rules. These wrappers keep
 * the page's `FormTreeQuestion` calling convention.
 */
export {
  findLayoutIssue,
  formatLayoutPlainCssHiddenSummary,
  formatRadioOptionSchemaMismatchSummary,
  sectionHasLayoutCoverageGaps,
  type LayoutIssue,
  type LayoutIssueKind,
} from '../../components/oasis/bespoke/layout/validation/validateSectionLayout';

export function collectSectionLayoutCoverageIssues(
  layoutInput: string | unknown | null | undefined,
  questions: FormTreeQuestion[]
): SectionLayoutCoverageIssues {
  return collectSectionLayoutCoverageIssuesShared(
    layoutInput,
    questions as Question[]
  );
}

export function validateLayoutJsonForSection(
  raw: string,
  questions: FormTreeQuestion[]
): LayoutValidationResult {
  return validateLayoutJsonForSectionShared(raw, questions as Question[]);
}

export function getSectionLayoutState(
  template: BespokeTemplateResponse | undefined,
  questions: FormTreeQuestion[]
): SectionLayoutState {
  if (!template) {
    return { kind: 'missing' };
  }
  if (!template.layout_json) {
    return {
      kind: 'invalid',
      template,
      reason: 'Template has no Roger Layout Schema.',
    };
  }
  const validation = validateLayoutJsonForSection(
    JSON.stringify(template.layout_json),
    questions
  );
  if (!validation.ok) {
    return { kind: 'invalid', template, reason: validation.error };
  }
  return { kind: 'valid', template };
}

/**
 * Merge refreshed rows into the catalog by id. Untouched rows keep their object
 * identity, and `is_rogeremr` is carried forward when a response leaves it null
 * (the pin never changes, so the earlier answer still holds).
 */
export function upsertBespokeTemplatesLocal(
  prev: readonly BespokeTemplateResponse[],
  saved: readonly BespokeTemplateResponse[]
): BespokeTemplateResponse[] {
  if (saved.length === 0) return [...prev];
  // A section holds one row, so a refreshed row with a new id replaces the old one.
  const savedIdBySection = new Map(
    saved.map(s => [bespokeTemplateLeafKey(s), s.id])
  );
  const byId = new Map(
    prev
      .filter(
        t => (savedIdBySection.get(bespokeTemplateLeafKey(t)) ?? t.id) === t.id
      )
      .map(t => [t.id, t])
  );
  for (const s of saved) {
    const prior = byId.get(s.id);
    byId.set(
      s.id,
      s.is_rogeremr == null && prior?.is_rogeremr != null
        ? { ...s, is_rogeremr: prior.is_rogeremr }
        : s
    );
  }
  return [...byId.values()];
}

export type { FormSchemaVersionPin } from './formSchemaVersionPin';
export {
  bespokeTemplateMatchesPin,
  filterBespokeTemplatesForPin,
  formSchemaVersionPinKey,
  formSchemaVersionPinKeyFromTemplate,
  pinFromFormSchemaVersion,
  pinsFromFormSchemaVersions,
  templatesBySectionForPin,
} from './formSchemaVersionPin';

export type SectionValidationStatus = 'pass' | 'fail' | 'missing';

export function sectionValidationStatusFromLayoutState(
  state: SectionLayoutState
): SectionValidationStatus {
  if (state.kind === 'valid') return 'pass';
  if (state.kind === 'invalid') return 'fail';
  return 'missing';
}

/** Validation modal: a section with no layout is incomplete. */
export function sectionValidationStatusForLayout(
  template: BespokeTemplateResponse | undefined,
  questions: FormTreeQuestion[]
): SectionValidationStatus {
  if (!template) {
    return 'missing';
  }
  return sectionValidationStatusFromLayoutState(
    getSectionLayoutState(template, questions)
  );
}

/**
 * Prefer section_order when present; append any tree keys the FSV omits.
 * Shared by Forms Manager sidebar and Validation Results.
 */
export function orderedFormTreeSectionIds(
  tree: Record<string, unknown>,
  sectionOrder: { section_id: string }[] | null | undefined
): string[] {
  if (sectionOrder && sectionOrder.length > 0) {
    const ordered = sectionOrder
      .map(item => item.section_id)
      .filter(sectionId => sectionId in tree);
    const seen = new Set(ordered);
    for (const key of Object.keys(tree)) {
      if (!seen.has(key)) {
        ordered.push(key);
        seen.add(key);
      }
    }
    if (ordered.length > 0) return ordered;
  }
  return Object.keys(tree);
}
